import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import dayjs from 'dayjs';

export interface ResponsibilityCategory {
  total: number;
  hasOverdue?: boolean;
  hasDueSoon?: boolean;
}

export interface MyResponsibilities {
  assets: ResponsibilityCategory;
  tasks: ResponsibilityCategory;
  controls: ResponsibilityCategory;
  risks: ResponsibilityCategory;
  incidents: ResponsibilityCategory;
  suppliers: ResponsibilityCategory;
  evidence: ResponsibilityCategory;
  totalCount: number;
}

export function useMyResponsibilities() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['my-responsibilities', profile?.org_id, profile?.user_id],
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async (): Promise<MyResponsibilities> => {
      if (!profile?.org_id || !profile?.user_id) {
        return {
          assets: { total: 0 },
          tasks: { total: 0 },
          controls: { total: 0 },
          risks: { total: 0 },
          incidents: { total: 0 },
          suppliers: { total: 0 },
          evidence: { total: 0 },
          totalCount: 0,
        };
      }

      const now = dayjs();

      // Fetch sites assigned to user
      const { data: sites } = await supabase
        .from('sites')
        .select('id')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id);

      // Fetch zones assigned to user
      const { data: zones } = await supabase
        .from('zones')
        .select('id')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id);

      // Fetch systems assigned to user
      const { data: systems } = await supabase
        .from('systems')
        .select('id')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id);

      // Fetch endpoints assigned to user
      const { data: endpoints } = await supabase
        .from('endpoints')
        .select('id')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id);

      // Fetch cloud services assigned to user
      const { data: cloudServices } = await supabase
        .from('cloud_services')
        .select('id')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id);

      // Fetch cloud resources assigned to user
      const { data: cloudResources } = await supabase
        .from('cloud_resources')
        .select('id')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id);

      const totalAssets = (sites?.length || 0) + (zones?.length || 0) + (systems?.length || 0) + 
                          (endpoints?.length || 0) + (cloudServices?.length || 0) + (cloudResources?.length || 0);

      // Fetch tasks assigned to user
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('id, status, due_next')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id)
        .not('status', 'in', '("Done","Skipped")');

      if (tasksError) throw tasksError;

      const activeTasks = tasks || [];
      const hasOverdueTasks = activeTasks.some(t => dayjs(t.due_next).isBefore(now));
      const hasDueSoonTasks = activeTasks.some(t => {
        const due = dayjs(t.due_next);
        return due.isAfter(now) && due.isBefore(now.add(7, 'days'));
      });

      // Fetch controls assigned to user
      const { data: controls, error: controlsError } = await supabase
        .from('controls')
        .select('id, status')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id)
        .in('status', ['not_implemented', 'in_progress']);

      if (controlsError) throw controlsError;

      const hasNotImplementedControls = (controls || []).some(c => c.status === 'not_implemented');

      // Fetch risks assigned to user
      const { data: risks, error: risksError } = await supabase
        .from('risks')
        .select('id, risk_score')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id)
        .in('status', ['identified', 'assessed', 'in_treatment']);

      if (risksError) throw risksError;

      // Risk urgency: score >= 20 is critical (red), >= 12 is high (yellow)
      const hasHighRisks = (risks || []).some(r => (r.risk_score || 0) >= 20);
      const hasMediumHighRisks = (risks || []).some(r => {
        const score = r.risk_score || 0;
        return score >= 12 && score < 20;
      });

      // Fetch incidents assigned to user
      const { data: incidents, error: incidentsError } = await supabase
        .from('incidents')
        .select('id, status')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id)
        .in('status', ['draft', 'investigating', 'containment', 'eradication', 'recovery']);

      if (incidentsError) throw incidentsError;

      // Incident urgency: containment/eradication = red, investigating = yellow
      const hasCriticalIncidents = (incidents || []).some(i => 
        i.status === 'containment' || i.status === 'eradication'
      );
      const hasActiveIncidents = (incidents || []).some(i => i.status === 'investigating');

      // Fetch suppliers assigned to user
      const { data: suppliers, error: suppliersError } = await supabase
        .from('suppliers')
        .select('id, next_review')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id);

      if (suppliersError) throw suppliersError;

      // Supplier urgency: overdue review = red, review within 30 days = yellow
      const hasOverdueSuppliers = (suppliers || []).some(s => 
        s.next_review && dayjs(s.next_review).isBefore(now)
      );
      const hasDueSoonSuppliers = (suppliers || []).some(s => {
        if (!s.next_review) return false;
        const review = dayjs(s.next_review);
        return review.isAfter(now) && review.isBefore(now.add(30, 'days'));
      });

      // Fetch evidence assigned to user
      const { data: evidence, error: evidenceError } = await supabase
        .from('evidence')
        .select('id')
        .eq('org_id', profile.org_id)
        .eq('owner_id', profile.user_id);

      if (evidenceError) throw evidenceError;

      const totalCount = 
        totalAssets +
        activeTasks.length +
        (controls?.length || 0) +
        (risks?.length || 0) +
        (incidents?.length || 0) +
        (suppliers?.length || 0) +
        (evidence?.length || 0);

      return {
        assets: { total: totalAssets },
        tasks: { 
          total: activeTasks.length,
          hasOverdue: hasOverdueTasks,
          hasDueSoon: hasDueSoonTasks,
        },
        controls: { 
          total: controls?.length || 0,
          hasDueSoon: hasNotImplementedControls,
        },
        risks: { 
          total: risks?.length || 0,
          hasOverdue: hasHighRisks,
          hasDueSoon: hasMediumHighRisks,
        },
        incidents: { 
          total: incidents?.length || 0,
          hasOverdue: hasCriticalIncidents,
          hasDueSoon: hasActiveIncidents,
        },
        suppliers: { 
          total: suppliers?.length || 0,
          hasOverdue: hasOverdueSuppliers,
          hasDueSoon: hasDueSoonSuppliers,
        },
        evidence: { total: evidence?.length || 0 },
        totalCount,
      };
    },
    enabled: !!profile?.org_id && !!profile?.user_id,
  });
}
